import { useCallback } from 'react';
import { parseAsInteger, parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs';

/**
 * Planning mode: the board arrives with something to place, and a click picks the slot for it.
 *
 * The mode says *what* is being planned; the slot itself is not here. A pick is a transient pointer
 * position with one owner (`DispositionBoardWidget`), so it lives in `useState` — nuqs is for
 * filters, modals and pagination. Everything needed to reconstruct the mode from a pasted link is
 * in the URL, so the link lands you here on the right day and you re-pick.
 *
 * The modals are **not** opened from here. They have their own `upsert-*` params
 * (`features/appointment-upsert`, `features/task-upsert`), and the bar's button calls those — so
 * there is still exactly one create path, the same one the work-order page uses.
 */

const MODES = ['schedule-appointment', 'schedule-task'] as const;

export type DispositionModeName = (typeof MODES)[number];

export const useDispositionModeParams = () =>
  useQueryStates(
    {
      'disposition-mode': parseAsStringLiteral(MODES),
      /** Appointment mode: which Auftrag is being given a Termin. */
      'work-order-id': parseAsString,
      /**
       * Appointment mode, the other way round: move *that* Termin to the picked slot. Mutually
       * exclusive with `work-order-id` — one creates, the other edits.
       */
      'appointment-id': parseAsInteger,
      /** Task mode: schedule *that* task. Absent means create a new one on the picked slot. */
      'task-id': parseAsInteger,
    },
    { history: 'replace', shallow: true, clearOnDefault: true },
  );

export interface DispositionMode {
  mode: DispositionModeName | null;
  /** Derived from the mode's presence — never a separate boolean param. */
  isPlanning: boolean;
  workOrderId: string | null;
  /** Set only when an existing Termin is being moved rather than a new one created. */
  appointmentId: number | null;
  taskId: number | null;
  startAppointment: (workOrderId: string) => void;
  startAppointmentReschedule: (appointmentId: number) => void;
  startTask: (taskId?: number) => void;
  exit: () => void;
}

export function useDispositionMode(): DispositionMode {
  const [params, setParams] = useDispositionModeParams();

  const startAppointment = useCallback(
    (workOrderId: string) =>
      setParams({
        'disposition-mode': 'schedule-appointment',
        'work-order-id': workOrderId,
        'appointment-id': null,
        'task-id': null,
      }),
    [setParams],
  );

  const startAppointmentReschedule = useCallback(
    (appointmentId: number) =>
      setParams({
        'disposition-mode': 'schedule-appointment',
        'work-order-id': null,
        'appointment-id': appointmentId,
        'task-id': null,
      }),
    [setParams],
  );

  const startTask = useCallback(
    (taskId?: number) =>
      setParams({
        'disposition-mode': 'schedule-task',
        'work-order-id': null,
        'appointment-id': null,
        'task-id': taskId ?? null,
      }),
    [setParams],
  );

  const exit = useCallback(
    () =>
      setParams({
        'disposition-mode': null,
        'work-order-id': null,
        'appointment-id': null,
        'task-id': null,
      }),
    [setParams],
  );

  const mode = params['disposition-mode'];

  return {
    mode,
    isPlanning: mode != null,
    workOrderId: params['work-order-id'],
    appointmentId: params['appointment-id'],
    taskId: params['task-id'],
    startAppointment,
    startAppointmentReschedule,
    startTask,
    exit,
  };
}
